import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DroppableProvided, DroppableStateSnapshot } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../modules';
import { copyContent, moveContent, removeContent, selectContent, setDefaultShop, setShops, switchContent } from '../../modules/editor';

import useAlert from '../../hooks/useAlert';
import useDebounce from '../../hooks/useDebounce';

import { Container, Contents } from '../../modules/editor/types';
import Content from './Content';
import ContentsListDropzone from './ContentsListDropzone';

import arrow_down from '../../assets/images/icon/arrow_down.png';
import arrow_up from '../../assets/images/icon/arrow_up.png';
import icon_copy from '../../assets/images/icon/copy.png';
import move from '../../assets/images/icon/move.png';
import icon_switch from '../../assets/images/icon/switch.png';
import trashcan from '../../assets/images/icon/trashcan.png';
import { TEXT_TEMPLATE } from '../../constants';
import { requestShops } from '../../modules/editor/service';

type Props = {
	list: Contents;
	container: Container;
	onDrop: (candrop: boolean) => void;
};

function ContentsList({ list, container, onDrop }: Props) {
	const dispatch = useDispatch();
	const Alert = useAlert();

	const [items, setItems] = useState([] as any);
	const [dbCanDrop, canDrop, setCanDrop] = useDebounce<boolean>(
		false,
		10
	);
	const [dbCanDropItem, canDropItem, setCanDropItem] = useDebounce<number>(
		-1,
		10
	);
	const [placeholderProps, setPlaceholderProps] = useState({} as any);

	const listRef = useRef(null);

	const [listHeight, setListHeight] = useState(0);

	const selectedContentId = useSelector((state: RootState) => state.editor.selectedContentId);
	const isContentsLoading = useSelector((state: RootState) => state.editor.ui.isContentsLoading);
	const { templateId, listId, isListDeleted } = useSelector((state: RootState) => state.editor.email);
	const hasNoList = listId === -1 || listId === undefined || isListDeleted;

	const contentListStyle = useMemo(() => {
		const style: Omit<Container, 'pallette' | 'commonStyle'> & { pallette?: Container['pallette'], commonStyle?: Container['commonStyle'] } = {
			...container,
			borderStyle: container.borderStyle.length === 0 ? 'solid' : container.borderStyle,
		};
		delete style.pallette;
		delete style.commonStyle;
		return style
	}, [container]);

	useEffect(() => {
		if (listRef?.current) {
			setListHeight(listRef?.current?.scrollHeight + 270);
		}
	}, [dbCanDrop])

	useEffect(() => {
		setItems(list);
	}, [list])

	useEffect(() => {
		onDrop(dbCanDrop);
	}, [dbCanDrop])

	const reOrder = (list:any, startIndex:any, endIndex:any) => {
		const result = Array.from(list);
		const [removed] = result.splice(startIndex, 1);
		result.splice(endIndex, 0, removed);

		setItems(result);
		dispatch(moveContent(result));
	};

	const getItemStyle = (draggableStyle:any, isDragging:any) => ({
		...draggableStyle,
		userSelect: 'none',
	});

	const onDragEnd = (result:any) => {
		// dropped outside the list(리스트 밖으로 드랍한 경우)
		if (!result.destination) {
			setCanDrop(false);
			return;
		}

		reOrder(
			items,
			result.source.index,
			result.destination.index
		);

		setCanDrop(false);
	}

	const onMouseOver = (e: any) => {
		if (canDrop) {
			return;
		}
		const { currentTarget } = e;
		currentTarget.classList.add('hover');
	}

	const onMouseOut = (e: any) => {
		const { currentTarget } = e;
		currentTarget.classList.remove('hover');
	}

	const selectItem = (e:any, id:number) => {
		e.stopPropagation();
		if (selectedContentId === id) {
			return;
		}
		dispatch(selectContent(id));
	}

	const movePrev = (e: any, index:number) => {
		e.stopPropagation();
		if (index === 0) {
			return;
		}

		reOrder(
			items,
			index,
			index-1
		);
	}

	const moveNext = (e: any, index:number) => {
		e.stopPropagation();
		if (index + 1 >= items.length) {
			return;
		}

		reOrder(
			items,
			index,
			index + 1
		);
	}

	const remove = (id:number) => {
		Alert.confirm(Alert?.msg?.BOX_REMOVE, () => {
			dispatch(removeContent(id));
		});
	}

	const copy = (index:number) => {
		dispatch(copyContent({ seq: index + 1, originalIndex: index }));
	}

	const onDragOver = (e:any) => {		
		e.preventDefault();		
		if (e.dataTransfer.types.includes('text/plain')) {
			setCanDrop(true);	
		}
		// dropEffect를 move로 설정.
		e.dataTransfer.dropEffect = "move";
	}

	const onDragLeave = (e:any) => {
		setCanDrop(false);
	}

	const onDragOverItem = (index:number, e:any) => {
		if (e.dataTransfer.types.includes('text/plain') && index !== canDropItem) {
			setCanDropItem(index);
		}
	}

	const onDragLeaveItem = () => {
		setCanDropItem(-1);
	}

	const getListClass = (isDragging:boolean) => {
		return isDragging ? 'content-list-dropzone dragging' : 'content-list-dropzone'
	}

	const onContainerDragStart = (e:any) => {
		setCanDrop(true);	
	}

	const onContainerDragUpdate = (e:any) => {		
		if (!e.destination) {
      return;
    }

    const draggedDOM:any = getDraggedDom(e.draggableId);

    if (!draggedDOM) {
      return;
    }
		
		const { clientHeight, clientWidth } = draggedDOM;
    const destinationIndex = e.destination.index;
    const sourceIndex = e.source.index;

		const updatedArray:any = Array.from(document.querySelectorAll('.content-list-dropzone > div'));
		if (sourceIndex < destinationIndex) {
      updatedArray.splice(sourceIndex, 1);
    }

    var clientY =
      updatedArray.slice(0, destinationIndex).reduce((total:any, curr:any) => {
				if (curr !== undefined) {
					const style = curr?.currentStyle || window?.getComputedStyle(curr);
					const marginBottom = parseFloat(style?.marginBottom);
					return total + curr?.clientHeight + marginBottom;
				} else {
					return 0;
				}
			}, 0);

		setPlaceholderProps({
			clientHeight,
			clientWidth,
			clientY,
			clientX: parseFloat(
				window.getComputedStyle(draggedDOM.parentNode).paddingLeft
			)
		});
	}

	const getDraggedDom = (draggableId: number) => {
		const domQuery = `[data-rbd-draggable-id='${draggableId}']`;
		const draggedDOM = document.querySelector(domQuery);

		return draggedDOM;
	};

	const switchCol = (id: number) => {
		dispatch(switchContent(id));
	}

	const prepareShopProductItem = async () => {
    const shops = await requestShops();
    dispatch(setShops(shops));
    dispatch(setDefaultShop(shops[0]));
	}

	return (
		<div
			className="content-list"
			style={items?.length === 0 ? {} : contentListStyle}
		>
			{isContentsLoading ? (<div className="content-list-loading"></div>) : (
				<div
					onDragOver={onDragOver}
					onDragLeave={onDragLeave}
					onDragEnd={onDragLeave}
					onDrop={onDragLeave}
					ref={listRef}
				>
					{
						items?.length === 0 ? (
							<div className="content-list-empty">
								<ContentsListDropzone
									canDrop={dbCanDrop}
									seq={0}
								/>
								{
									!dbCanDrop && (
										<p>
											오른쪽 <strong>상자</strong> 메뉴에서 상자를 끌어다 놓으세요. 👉
										</p>
									)
								}
							</div>
						) : (
							<DragDropContext
								onDragEnd={onDragEnd}
								onDragStart={onContainerDragStart}
								onDragUpdate={onContainerDragUpdate}
							>
								<Droppable droppableId="contentList">
									{(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
										<div
											{...provided.droppableProps}
											ref={provided.innerRef}
											className={getListClass(snapshot.isDraggingOver)}
										>
											{items?.map((item: any, index: number) => (
												<div key={item.id} style={{ position: 'relative', padding: dbCanDropItem === index ? index === 0 ? '54px 0 54px 0' : '0 0 54px 0' : '0 0' }} >
													<div
														onDragOver={(e) => onDragOverItem(index, e)}
														onDragLeave={onDragLeaveItem}
														onDragEnd={onDragLeaveItem}
														onDrop={onDragLeaveItem}
													>
														<ContentsListDropzone
															canDrop={dbCanDropItem === index}
															seq={index}
														/>
														<Draggable
															index={index}
															key={item.id}
															draggableId={`${item.id}`}
														>
															{
																(provided, snapshot) => (
																	<div>
																		{templateId === TEXT_TEMPLATE ? (
																			<Content
																				contentItem={item}
																				key={index}
																			/>
																		) : (
																			<div
																				className={selectedContentId === item.id ? 'content-wrapper selected' : snapshot.isDragging ? 'content-wrapper dragging' : 'content-wrapper'}
																				onMouseOver={onMouseOver}
																				onMouseOut={onMouseOut}
																				onClick={async (e) => {
																					if (item.content.category === 'shop' && hasNoList) {
																						prepareShopProductItem();
																					};
																					selectItem(e, item.id);
																				}}

																				ref={provided.innerRef}
																				{...provided.draggableProps}
																				style={getItemStyle(
																					provided.draggableProps.style,
																					snapshot.isDragging
																				)}
																			>
																				<Content
																					contentItem={item}
																					key={index}
																				/>
																				<div className="content-handler">
																					<div
																						{...provided.dragHandleProps}
																						className="move"
																					>
																						<img src={move} alt="move" width="30" />
																					</div>
																					<button onClick={(e) => { movePrev(e, index) }} className="prev">
																						<img src={arrow_up} alt="arrow_up" width="30" />
																					</button>
																					<button onClick={(e) => { moveNext(e, index) }} className="next">
																						<img src={arrow_down} alt="arrow_down" width="30" />
																					</button>
																					<button onClick={(e: any) => { copy(index); e.stopPropagation() }} className="copy">
																						<img src={icon_copy} alt="copy" width="30" />
																					</button>
																					<button onClick={(e: any) => { remove(item.id); e.stopPropagation() }} className="remove">
																						<img src={trashcan} alt="trashcan" width="30" />
																					</button>
																				</div>
																				{
																					selectedContentId === item.id && item?.content?.column?.length === 2 && (
																						<div className="content-switch">
																							<button onClick={() => { switchCol(item.id) }}>
																								<img src={icon_switch} alt="switch" width="34" />
																							</button>
																						</div>
																					)
																				}
																			</div>
																		)}
																	</div>
																)
															}
														</Draggable>
													</div>
												</div>
											))}

											{provided.placeholder}
											{snapshot.isDraggingOver && (
												<div
													className="placeholder"
													style={{
														top: placeholderProps.clientY,
														left: placeholderProps.clientX,
														height: placeholderProps.clientHeight,
														width: placeholderProps.clientWidth
													}}
												>
													<p>
														여기로 상자를 이동합니다.
													</p>
												</div>
											)}
										</div>
									)}
								</Droppable>
							</DragDropContext>
						)
					}
				</div>
			)}
		</div>
	);
}

export default ContentsList;